import { useEffect, useMemo, useRef, useState } from "react";
import { Mic, Loader2, Info } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { useAccounts } from "@/api/accounts";
import { useCategories } from "@/api/categories";
import { useCreateTransaction, useParseVoiceTransaction } from "@/api/transactions";
import { useAuth } from "@/context/AuthContext";
import { useVoiceRecorder, isVoiceRecordingSupported } from "@/lib/useVoiceRecorder";
import { notifyTransactionCreated } from "@/lib/interstitialAdTrigger";
import { todayISO, formatCurrency } from "@/lib/format";
import { ApiError } from "@/lib/apiClient";
import { cn } from "@/lib/cn";
import { CategoryStep } from "./CategoryStep";
import { AccountStep } from "./AccountStep";
import { WizardHeader } from "./WizardHeader";
import { PremiumBlurOverlay } from "@/components/ui/PremiumBlurOverlay";
import type { RecurrenceOption, TransactionStatus } from "@/api/types";
import styles from "./VoiceQuickAdd.module.css";
import wizardStyles from "./wizardSteps.module.css";
import formStyles from "@/styles/form.module.css";

type Phase = "recording" | "processing" | "review";

const MAX_RECORDING_SECONDS = 10;
const WAVEFORM_BARS = 5;
const EXAMPLE_PHRASE = "Gastei 50 reais em comida, na categoria mercado";

interface VoiceQuickAddProps {
  isOpen: boolean;
  onClose: () => void;
}

export function VoiceQuickAdd({ isOpen, onClose }: VoiceQuickAddProps) {
  const { user } = useAuth();
  const { data: categories } = useCategories();
  const { data: accounts } = useAccounts();
  const stoppingRef = useRef(false);
  const barRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const { isRecording, start, stop, getLevel } = useVoiceRecorder({
    // Assim que a pessoa para de falar, corta sozinho - economiza tempo de gravação (e custo de transcrição).
    onSilence: () => handleStopRecording(),
  });
  const parseVoice = useParseVoiceTransaction();
  const createTransaction = useCreateTransaction();
  const supported = isVoiceRecordingSupported();

  const [phase, setPhase] = useState<Phase>("recording");
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);

  const [transcript, setTranscript] = useState("");
  const [type, setType] = useState<"income" | "expense">("expense");
  const [amount, setAmount] = useState<number | "">("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(todayISO());
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [status, setStatus] = useState<TransactionStatus>("paid");
  const [recurrence, setRecurrence] = useState<RecurrenceOption>("none");
  const [installmentTotal, setInstallmentTotal] = useState<number | "">(12);

  // Reseta tudo e já dispara a gravação toda vez que o modal abre.
  useEffect(() => {
    // reseta tanto ao abrir quanto ao fechar (mesmo sem salvar), pra nunca sobrar
    // valor digitado da vez anterior quando abrir de novo
    stoppingRef.current = false;
    setPhase("recording");
    setStep(0);
    setError(null);
    setSaveError(null);
    setTranscript("");
    setType("expense");
    setAmount("");
    setDescription("");
    setDate(todayISO());
    setCategoryId(null);
    setAccountId(null);
    setStatus("paid");
    setRecurrence("none");
    setInstallmentTotal(12);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Free não tem acesso ao recurso - nem pede permissão de microfone, só mostra o paywall.
    if (!user?.isPremium) return;

    if (!supported) {
      setError("Seu navegador não suporta gravação de áudio.");
      return;
    }
    start().catch(() => setError("Não consegui acessar o microfone. Verifique a permissão do navegador."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Corta a gravação em 10s pra ninguém esquecer o áudio gravando e gastar tokens da Groq à toa
  // (o corte por silêncio no useVoiceRecorder normalmente já para antes disso).
  useEffect(() => {
    if (!isRecording) return;
    setElapsed(0);
    let seconds = 0;
    const interval = setInterval(() => {
      seconds += 1;
      setElapsed(seconds);
      if (seconds >= MAX_RECORDING_SECONDS) {
        clearInterval(interval);
        handleStopRecording();
      }
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  // Anima as barrinhas de volume em tempo real (via ref direto, sem re-renderizar a cada frame).
  useEffect(() => {
    if (!isRecording) return;
    let frame: number;
    const animate = () => {
      const level = getLevel();
      const t = Date.now();
      barRefs.current.forEach((bar, i) => {
        if (!bar) return;
        const wobble = 0.55 + 0.45 * Math.sin(t / 160 + i * 1.1);
        const pct = 16 + level * 84 * wobble;
        bar.style.height = `${Math.min(100, pct)}%`;
      });
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [isRecording, getLevel]);

  // Se fechar o modal no meio da gravação, libera o microfone.
  useEffect(() => {
    if (isOpen || !isRecording) return;
    stop().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const selectableCategories = useMemo(() => categories?.filter((c) => c.type === type) ?? [], [categories, type]);
  const allAccounts = useMemo(() => accounts ?? [], [accounts]);
  const selectedIsCard = allAccounts.find((a) => a.id === accountId)?.type === "credit_card";

  const handleRetry = () => {
    stoppingRef.current = false;
    setError(null);
    setPhase("recording");
    start().catch(() => setError("Não consegui acessar o microfone. Verifique a permissão do navegador."));
  };

  const handleStopRecording = async () => {
    // Corte por silêncio e o limite de 10s podem disparar quase juntos - só processa uma vez.
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    setError(null);
    const blob = await stop();
    setPhase("processing");
    try {
      const result = await parseVoice.mutateAsync(blob);
      setTranscript(result.transcript);
      setType(result.type);
      setDescription(result.description);
      setAmount(result.amount);
      setDate(result.date);
      setCategoryId(result.categoryId);
      setPhase("review");
      setStep(0);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Não consegui processar o áudio.");
      setPhase("recording");
    }
  };

  const handleTypeChange = (t: "income" | "expense") => {
    setType(t);
    setCategoryId(null);
  };

  const handleSelectCategory = (id: string) => {
    setCategoryId(id);
    setTimeout(() => setStep(1), 260);
  };

  const handleSelectAccount = (id: string) => {
    setAccountId(id);
    setTimeout(() => setStep(2), 260);
  };

  const canSave = amount !== "" && Number(amount) > 0 && description.trim() !== "" && !!accountId && !!categoryId && !!date;

  const handleSave = async () => {
    if (!accountId || !categoryId || amount === "") return;
    setSaveError(null);
    try {
      await createTransaction.mutateAsync({
        type,
        accountId,
        categoryId,
        amount: Number(amount),
        description: description.trim(),
        date,
        status,
        recurrence,
        installmentTotal: recurrence === "installments" ? Number(installmentTotal) : undefined,
      });
      notifyTransactionCreated(!!user?.isPremium);
      onClose();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Não consegui salvar o lançamento.");
    }
  };

  if (!user?.isPremium) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Fale o ganho ou gasto">
        <PremiumBlurOverlay description="Fale o ganho ou gasto em voz alta e deixe a IA preencher tudo pra você. Disponível no plano Premium.">
          <div className={styles.centerWrap}>
            <button type="button" className={styles.micButton} disabled aria-hidden>
              <Mic size={30} strokeWidth={2} />
            </button>
            <p className={styles.recordingTimer}>0s</p>
            <p className={styles.recordingHint}>Toque pra parar</p>
          </div>
        </PremiumBlurOverlay>
      </Modal>
    );
  }

  const title =
    phase === "recording" ? "Fale o ganho ou gasto" : phase === "processing" ? "Um instante..." : ["Categoria", "Conta", "Detalhes"][step];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      {phase === "recording" &&
        (error ? (
          <div className={styles.centerWrap}>
            <p className={wizardStyles.errorText}>{error}</p>
            <Button type="button" onClick={handleRetry} className={styles.retryButton}>
              Tentar de novo
            </Button>
          </div>
        ) : (
          <div className={styles.centerWrap}>
            <button
              type="button"
              className={cn(styles.micButton, isRecording && styles.micButtonRecording)}
              onClick={handleStopRecording}
              disabled={!isRecording}
              aria-label={isRecording ? "Parar gravação" : "Preparando microfone"}
            >
              <span className={styles.micRing} aria-hidden />
              <Mic size={30} strokeWidth={2} />
            </button>
            {isRecording && (
              <div className={styles.waveform} aria-hidden>
                {Array.from({ length: WAVEFORM_BARS }).map((_, i) => (
                  <span
                    key={i}
                    ref={(el) => {
                      barRefs.current[i] = el;
                    }}
                    className={styles.waveformBar}
                  />
                ))}
              </div>
            )}
            <p className={styles.recordingTimer}>{isRecording ? `${elapsed}s / ${MAX_RECORDING_SECONDS}s` : "Preparando..."}</p>
            <p className={cn(styles.recordingHint, isRecording && styles.recordingHintActive)}>
              {isRecording ? "Toque pra parar" : "Aguardando permissão do microfone"}
            </p>
            <p className={styles.exampleHint}>
              Fale assim: <em>"{EXAMPLE_PHRASE}"</em>
            </p>
            <p className={styles.disclaimer}>
              <Info size={11} strokeWidth={2.2} />
              O reconhecimento pode errar às vezes, dependendo de como você fala
            </p>
          </div>
        ))}

      {phase === "processing" && (
        <div className={styles.centerWrap}>
          <Loader2 size={30} className={styles.spinnerIcon} />
          <p className={styles.processingText}>Entendendo o que você falou...</p>
        </div>
      )}

      {phase === "review" && (
        <div className={wizardStyles.review}>
          <WizardHeader step={step} totalSteps={3} onBack={step > 0 ? () => setStep((s) => s - 1) : undefined} />

          {transcript && <p className={styles.transcriptLine}>🎙️ "{transcript}"</p>}
          <p className={wizardStyles.previewLine}>
            {formatCurrency(Number(amount) || 0)} · {description || "—"}
          </p>

          {step === 0 && (
            <div className={wizardStyles.stepBlock}>
              <div className={wizardStyles.typeToggle}>
                <button
                  type="button"
                  onClick={() => handleTypeChange("expense")}
                  className={cn(wizardStyles.typeButton, type === "expense" && wizardStyles.typeButtonExpenseActive)}
                >
                  Despesa
                </button>
                <button
                  type="button"
                  onClick={() => handleTypeChange("income")}
                  className={cn(wizardStyles.typeButton, type === "income" && wizardStyles.typeButtonIncomeActive)}
                >
                  Receita
                </button>
              </div>
              <CategoryStep categories={selectableCategories} categoryId={categoryId} onSelect={handleSelectCategory} />
            </div>
          )}

          {step === 1 && <AccountStep accounts={allAccounts} accountId={accountId} onSelect={handleSelectAccount} />}

          {step === 2 && (
            <div className={wizardStyles.stepBlock}>
              <h3 className={wizardStyles.stepTitle}>Últimos detalhes</h3>
              <Input label="Descrição" value={description} onChange={(e) => setDescription(e.target.value)} />
              <div className={formStyles.row2}>
                <CurrencyInput
                  label="Valor"
                  value={amount === "" ? undefined : amount}
                  onChange={(v) => setAmount(v ?? "")}
                />
                <Input label="Data" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              {selectedIsCard && (
                <p className={wizardStyles.cardHint}>
                  Lançamento no cartão — a fatura soma automaticamente. Quando for pagá-la, use{" "}
                  <strong>"Pagar fatura"</strong> em Contas e cartões, não crie outro lançamento aqui.
                </p>
              )}
              <div className={wizardStyles.statusToggle}>
                <button
                  type="button"
                  onClick={() => setStatus("paid")}
                  className={cn(wizardStyles.statusOption, status === "paid" && wizardStyles.statusOptionPaidActive)}
                >
                  Pago/Recebido
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("pending")}
                  className={cn(wizardStyles.statusOption, status === "pending" && wizardStyles.statusOptionPendingActive)}
                >
                  Pendente
                </button>
              </div>
              <Select label="Recorrência" value={recurrence} onChange={(v) => setRecurrence(v as RecurrenceOption)}>
                <option value="none">Lançamento único</option>
                <option value="fixed">Fixo mensal (12 meses)</option>
                <option value="installments">Parcelado</option>
              </Select>
              {recurrence === "installments" && (
                <Input
                  label="Número de parcelas"
                  type="number"
                  min={2}
                  max={60}
                  value={installmentTotal}
                  onChange={(e) => setInstallmentTotal(e.target.value === "" ? "" : Number(e.target.value))}
                />
              )}
              {saveError && <p className={wizardStyles.errorText}>{saveError}</p>}
              <Button
                type="button"
                onClick={handleSave}
                isLoading={createTransaction.isPending}
                disabled={!canSave}
                className={formStyles.fullWidth}
              >
                Salvar lançamento
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
